/**
 * Account-dashboard model — the wire shape returned by
 * `GET /api/mobile/v1/account/dashboard` and computed by the shared
 * `lib/account-dashboard.ts` (which also drives the mobile Home screen).
 *
 * Mirrors the at-a-glance summary the web dashboard renders at
 * `app/(app)/dashboard/page.tsx`: counts (active projects, docs this month,
 * unique workers), expiry warnings (worker certs + company documents), and a
 * top-3 recent-projects strip with traffic-light compliance health.
 *
 * Navigation is platform-neutral: items carry a semantic `target`, never a
 * URL. Web maps targets to hrefs; mobile maps them to navigator routes.
 */
import { z } from "zod";

export const DASHBOARD_HEALTH_STATUSES = [
  "green",
  "amber",
  "red",
] as const;

export const DashboardHealthStatusSchema = z.enum(DASHBOARD_HEALTH_STATUSES);
export type DashboardHealthStatus = z.infer<
  typeof DashboardHealthStatusSchema
>;

export const DashboardProjectSchema = z.object({
  id: z.string(),
  title: z.string(),
  siteAddress: z.string().nullable(),
  /** Compliance health derived from documents + incidents. */
  health: DashboardHealthStatusSchema,
  /** Human-readable reasons behind a non-green status. */
  healthIssues: z.array(z.string()),
  /** Documents attached to the project (any version). Drives "no docs yet". */
  documentCount: z.number().int().nonnegative(),
  /** ISO 8601 timestamp of last update — useful for sort + display. */
  updatedAt: z.string(),
});
export type DashboardProject = z.infer<typeof DashboardProjectSchema>;

export const DashboardCountsSchema = z.object({
  activeProjects: z.number().int().nonnegative(),
  totalProjects: z.number().int().nonnegative(),
  archivedProjects: z.number().int().nonnegative(),
  /** Document versions created in the current calendar month. */
  docsThisMonth: z.number().int().nonnegative(),
  /** Unique worker names across all the user's projects. */
  uniqueWorkers: z.number().int().nonnegative(),
});
export type DashboardCounts = z.infer<typeof DashboardCountsSchema>;

export const DashboardExpiryAlertsSchema = z.object({
  /** Worker certifications expiring within 90 days or already expired. */
  expiringCerts: z.number().int().nonnegative(),
  /** Company documents (insurance, accreditations) with the same window. */
  expiringCompanyDocs: z.number().int().nonnegative(),
});
export type DashboardExpiryAlerts = z.infer<
  typeof DashboardExpiryAlertsSchema
>;

export const DashboardGreetingSchema = z.object({
  /** "Good morning" / "Good afternoon" / "Good evening" — server-rendered
   *  so the greeting tracks UK wall-clock time, not the device's. */
  greeting: z.string(),
  /** Best-effort first name (Clerk firstName → username → email local-part →
   *  companyName first word → "there"). */
  firstName: z.string(),
});
export type DashboardGreeting = z.infer<typeof DashboardGreetingSchema>;

export const DashboardPlanInfoSchema = z.object({
  /** Plan tier — e.g. "starter" / "pro_monthly" / "pro_annual" / "business". */
  tier: z.string(),
  /** True for any tier >= pro. */
  isProPlus: z.boolean(),
  canCreateProject: z.boolean(),
  docGenerationsUsed: z.number().int().nonnegative(),
});
export type DashboardPlanInfo = z.infer<typeof DashboardPlanInfoSchema>;

export const DashboardFlagsSchema = z.object({
  /** True when the account has no projects yet (drives the first-run UX). */
  isFirstTime: z.boolean(),
  /** True when at least one project is ARCHIVED. */
  hasCompletedProject: z.boolean(),
  /** True when there's at least one project missing all documents. */
  hasNeedsAttention: z.boolean(),
});
export type DashboardFlags = z.infer<typeof DashboardFlagsSchema>;

export const AccountDashboardModelSchema = z.object({
  greeting: DashboardGreetingSchema,
  counts: DashboardCountsSchema,
  expiry: DashboardExpiryAlertsSchema,
  plan: DashboardPlanInfoSchema,
  flags: DashboardFlagsSchema,
  /** Up to 3 most-recently-updated ACTIVE projects with health computed. */
  recentProjects: z.array(DashboardProjectSchema),
  /** Subset of `recentProjects` with no documents — surfaced as a top
   *  "needs attention" strip. Always a subset by-id. */
  needsAttention: z.array(DashboardProjectSchema),
});
export type AccountDashboardModel = z.infer<typeof AccountDashboardModelSchema>;
